import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import clsx from 'clsx';

export interface ContextMenuItem {
  // `divider: true` renders a separator and ignores all other fields.
  divider?: boolean;
  label?: string;
  onClick?: () => void;
  disabled?: boolean;
  danger?: boolean;
  icon?: React.ReactNode;
  hint?: string;
}

interface Props {
  open: boolean;
  // Cursor position (clientX/clientY). The menu flips on-screen if it would
  // overflow the viewport.
  x: number;
  y: number;
  items: ContextMenuItem[];
  onClose: () => void;
  ariaLabel?: string;
}

const MENU_MIN_WIDTH = 180;
const VIEWPORT_PAD = 6;

export default function ContextMenu({ open, x, y, items, onClose, ariaLabel }: Props) {
  const menuRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({ left: x, top: y });

  useLayoutEffect(() => {
    if (!open) return;
    const el = menuRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    let left = x;
    let top = y;
    if (left + rect.width + VIEWPORT_PAD > vw) left = Math.max(VIEWPORT_PAD, vw - rect.width - VIEWPORT_PAD);
    if (top + rect.height + VIEWPORT_PAD > vh) top = Math.max(VIEWPORT_PAD, vh - rect.height - VIEWPORT_PAD);
    setPos({ left, top });
  }, [open, x, y, items.length]);

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const actionable = items.filter((it) => !it.divider);

  return (
    <div
      className="fixed inset-0 z-50"
      onClick={onClose}
      onContextMenu={(e) => {
        e.preventDefault();
        onClose();
      }}
    >
      <div
        ref={menuRef}
        role="menu"
        aria-label={ariaLabel}
        className="absolute flex flex-col overflow-hidden rounded-md border border-[var(--border-strong)] bg-[var(--popover-bg)] py-1 shadow-[var(--shadow-md)]"
        style={{ left: pos.left, top: pos.top, minWidth: MENU_MIN_WIDTH }}
        onClick={(e) => e.stopPropagation()}
      >
        {items.map((it, i) =>
          it.divider ? (
            <div key={`d${i}`} className="my-1 h-px bg-[var(--border)]" />
          ) : (
            <button
              key={it.label ?? i}
              type="button"
              disabled={it.disabled}
              role="menuitem"
              onClick={() => {
                if (it.disabled) return;
                it.onClick?.();
                onClose();
              }}
              className={clsx(
                'flex items-center gap-2 px-3 py-[6px] text-left text-[0.85rem]',
                it.disabled
                  ? 'cursor-not-allowed text-[var(--text-faint)]'
                  : it.danger
                    ? 'text-[var(--today-fg)] hover:bg-[var(--bg-hover)]'
                    : 'text-[var(--text)] hover:bg-[var(--bg-hover)]'
              )}
            >
              {it.icon && <span className="grid h-[14px] w-[14px] place-items-center text-[var(--text-muted)]">{it.icon}</span>}
              <span className="flex-1">{it.label}</span>
              {it.hint && (
                <span className="ml-2 text-[0.72rem] text-[var(--text-faint)]">{it.hint}</span>
              )}
            </button>
          )
        )}
        {actionable.length === 0 && (
          <div className="px-3 py-2 text-[0.78rem] text-[var(--text-faint)]">No actions.</div>
        )}
      </div>
    </div>
  );
}
